import { X, Building2, User, MapPin, CreditCard, StickyNote } from "lucide-react";
import { US_STATES, formatPhone, formatZip } from "@/lib/formHelpers";
import { CLIENTS } from "@/constants/testIds";

const STATUS_COLUMNS = [
  { key: "prospect", label: "Prospect" },
  { key: "trial", label: "Trial" },
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "churned", label: "Churned" },
];

const PLAN_LABELS = { starter: "Starter", front_office: "Front Office", multi_location: "Multi-Location", custom: "Custom" };
const PLAN_HINTS  = { starter: "$397 / mo", front_office: "$697 / mo", multi_location: "$997 / mo", custom: "Custom pricing" };

function Section({ icon, title, subtitle, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0F2340] text-white">
          {icon}
        </span>
        <div>
          <div className="text-sm font-semibold text-neutral-900 leading-tight">{title}</div>
          {subtitle && <div className="text-xs text-neutral-500">{subtitle}</div>}
        </div>
      </div>
      <div className="pl-9">{children}</div>
    </div>
  );
}

function Field({ label, required, hint, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs font-semibold tracking-widest uppercase text-neutral-500">
          {label}
          {required && <span className="text-[#E24E1B] ml-0.5">*</span>}
        </span>
        {hint && <span className="text-[11px] text-neutral-400 font-normal normal-case tracking-normal">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * Add-Client modal — extracted from Clients.jsx (Wave 2 component split, v16).
 * Purely presentational — parent owns `form`, `submit`, `submitting`, `industries`.
 */
export default function AddClientModal({ form, setForm, submitting, industries, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 md:p-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl shadow-2xl border border-neutral-200 my-4"
           onClick={(e) => e.stopPropagation()} data-testid="clients-add-form-modal">
        <div className="flex items-start justify-between px-8 pt-8 pb-5 border-b border-neutral-100">
          <div>
            <div className="overline mb-2 text-neutral-500">New client</div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Add a client manually</h2>
            <p className="text-sm text-neutral-500 mt-1">
              Fields marked <span className="text-[#E24E1B] font-semibold">*</span> are required. Everything else can be filled in later.
            </p>
          </div>
          <button type="button" onClick={onClose}
                  className="p-2 -mt-2 -mr-2 text-neutral-400 hover:text-neutral-900 transition"
                  aria-label="Close" data-testid="clients-add-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-8 py-6 space-y-8">
          <Section icon={<Building2 className="w-4 h-4" />} title="Business" subtitle="Who is this client?">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Business name" required className="md:col-span-2">
                <input required data-testid={CLIENTS.formBusinessName} value={form.business_name}
                       onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                       className="lf-input" placeholder="e.g. Rapid Response Plumbing" autoFocus />
              </Field>
              <Field label="Industry">
                <select data-testid={CLIENTS.formIndustry} value={form.industry}
                        onChange={(e) => setForm({ ...form, industry: e.target.value })} className="lf-input">
                  <option value="">Select an industry…</option>
                  {industries.map((i) => (<option key={i} value={i}>{i}</option>))}
                  <option value="Other">Other</option>
                </select>
              </Field>
              <Field label="Website (existing, if any)" hint="Leave blank if they have no website.">
                <input data-testid={CLIENTS.formWebsite} value={form.website_url}
                       onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                       className="lf-input" placeholder="rapidresponse.com" />
              </Field>
            </div>
          </Section>

          <Section icon={<User className="w-4 h-4" />} title="Primary contact" subtitle="Who do you talk to at this business?">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Contact name">
                <input data-testid={CLIENTS.formContactName} value={form.contact_name}
                       onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                       className="lf-input" placeholder="Jane Doe" />
              </Field>
              <Field label="Email">
                <input data-testid={CLIENTS.formEmail} type="email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="lf-input" placeholder="owner@business.com" />
              </Field>
              <Field label="Phone" hint="US format only — auto-formats as you type.">
                <input data-testid={CLIENTS.formPhone} inputMode="tel" value={form.phone}
                       onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                       className="lf-input font-mono" placeholder="(555) 123-4567" maxLength={14} />
              </Field>
            </div>
          </Section>

          <Section icon={<MapPin className="w-4 h-4" />} title="Location" subtitle="Where is the business physically located?">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <Field label="Street address" className="md:col-span-6">
                <input data-testid={CLIENTS.formAddress} value={form.address}
                       onChange={(e) => setForm({ ...form, address: e.target.value })}
                       className="lf-input" placeholder="123 Main St." />
              </Field>
              <Field label="City" className="md:col-span-3">
                <input data-testid={CLIENTS.formCity} value={form.city}
                       onChange={(e) => setForm({ ...form, city: e.target.value })}
                       className="lf-input" placeholder="Austin" />
              </Field>
              <Field label="State" className="md:col-span-2">
                <select data-testid={CLIENTS.formState} value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })} className="lf-input">
                  <option value="">—</option>
                  {US_STATES.map(([code, name]) => (
                    <option key={code} value={code}>{code} — {name}</option>
                  ))}
                </select>
              </Field>
              <Field label="ZIP" className="md:col-span-1">
                <input data-testid={CLIENTS.formZip} inputMode="numeric" value={form.zip}
                       onChange={(e) => setForm({ ...form, zip: formatZip(e.target.value) })}
                       className="lf-input font-mono" placeholder="78701" maxLength={10} />
              </Field>
            </div>
          </Section>

          <Section icon={<CreditCard className="w-4 h-4" />} title="Plan & pipeline" subtitle="How should this client be tracked?">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Pipeline status">
                <select data-testid={CLIENTS.formStatus} value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })} className="lf-input">
                  {STATUS_COLUMNS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Plan tier" hint={PLAN_HINTS[form.plan_tier] || ""}>
                <select data-testid={CLIENTS.formPlan} value={form.plan_tier}
                        onChange={(e) => setForm({ ...form, plan_tier: e.target.value })} className="lf-input">
                  {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Monthly recurring revenue">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-mono text-sm pointer-events-none">$</span>
                  <input data-testid={CLIENTS.formMrr} type="number" min="0" step="1" value={form.mrr}
                         onChange={(e) => setForm({ ...form, mrr: e.target.value })}
                         className="lf-input font-mono" style={{ paddingLeft: 26 }} placeholder="397" />
                </div>
              </Field>
            </div>
          </Section>

          <Section icon={<StickyNote className="w-4 h-4" />} title="Internal notes" subtitle="Only visible to your team.">
            <Field label="Notes">
              <textarea data-testid={CLIENTS.formNotes} value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="lf-input min-h-[96px] resize-y"
                        placeholder="Referral source, key objections, follow-up context…" rows={4} />
            </Field>
          </Section>

          <div className="flex items-center justify-between gap-3 pt-6 border-t border-neutral-100 -mx-8 px-8 -mb-6 pb-6 bg-neutral-50/60">
            <div className="text-xs text-neutral-500">
              You can edit every field later from the client&apos;s detail page.
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="lf-btn-outline" data-testid="clients-add-form-cancel">Cancel</button>
              <button data-testid={CLIENTS.formSubmit} type="submit" disabled={submitting}
                      className="lf-btn-primary min-w-[140px]">
                {submitting ? "Adding…" : "Add client"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
